import React, {
  createContext,
  useCallback,
  use,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import type { AuthUser } from "../api/types";
import { authClient, hasAppRole } from "../lib/auth";

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Re-reads the session from the server, bypassing the 5-minute cookie cache. */
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

type SessionUser = NonNullable<typeof authClient.$Infer.Session>["user"];

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const toAuthUser = (user: SessionUser | undefined): AuthUser | null =>
  hasAppRole(user)
    ? {
        id: user.id,
        email: user.email,
        role: user.role,
        firstName: user.firstName,
        lastName: user.lastName,
        acceptedPrivacyPolicyVersion: user.acceptedPrivacyPolicyVersion ?? undefined,
      }
    : null;

/**
 * Exposes better-auth's session (authClient.useSession) as the app's AuthUser.
 * Redirects are left to the route guards (ProtectedRoute, RootRedirect), so a
 * signed-out session navigates to login without reloading the page.
 */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { data: session, isPending, refetch } = authClient.useSession();

  const refresh = useCallback(
    () => refetch({ query: { disableCookieCache: true } }),
    [refetch],
  );

  // The HTTP client reports expired or invalid sessions; re-reading the session
  // lets the guards send the user to login.
  useEffect(() => {
    const handleSessionExpired = () => {
      void refresh();
    };
    window.addEventListener("auth:sessionExpired", handleSessionExpired);
    return () => window.removeEventListener("auth:sessionExpired", handleSessionExpired);
  }, [refresh]);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authClient.signOut();
    } catch (error) {
      console.warn("AuthContext: sign-out request failed, clearing local state anyway", error);
    }

    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {
      // Storage can be unavailable (private mode); nothing else to clear.
    }
  }, []);

  const user = useMemo(() => toAuthUser(session?.user), [session?.user]);

  const value = useMemo<AuthContextType>(
    () => ({ user, isAuthenticated: !!user, isLoading: isPending, refresh, logout }),
    [user, isPending, refresh, logout],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
};

export const useAuth = (): AuthContextType => {
  const context = use(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
